import { PepLogo } from "../brand/PepLogo";
import { DomainIcon } from "../../home/HomeIcons";
import { WHY_ECOSYSTEM_MODULES } from "../../why/why-content";
import type { PepLocale } from "../../i18n/types";

export interface WhyEcosystemDiagramProps {
  centerLabel: string;
  locale: PepLocale;
  /** Optional CSS class on root (e.g. contact hero sizing). */
  className?: string;
}

/**
 * Shared ITaaS Suite ecosystem orbit — used by Why ITaaS and Contact heroes.
 * Styles: pep-why.css (`.pep-why-eco*`). Do not fork animation CSS.
 */
export function WhyEcosystemDiagram({
  centerLabel,
  locale,
  className = "",
}: WhyEcosystemDiagramProps) {
  const count = WHY_ECOSYSTEM_MODULES.length;
  const cx = 200;
  const cy = 200;
  const radius = 158;
  const gradientId = "why-eco-link-shared";

  return (
    <div className={`pep-why-eco ${className}`.trim()} aria-hidden="true">
      <div className="pep-why-eco__halo" />
      <div className="pep-why-eco__ring pep-why-eco__ring--outer" />
      <div className="pep-why-eco__ring pep-why-eco__ring--spin" />

      <svg className="pep-why-eco__links" viewBox="0 0 400 400" role="presentation">
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(147,197,253,0.15)" />
            <stop offset="50%" stopColor="rgba(191,219,254,0.55)" />
            <stop offset="100%" stopColor="rgba(147,197,253,0.15)" />
          </linearGradient>
        </defs>
        {WHY_ECOSYSTEM_MODULES.map((mod, i) => {
          const angle = ((i / count) * 360 - 90) * (Math.PI / 180);
          const x = cx + Math.cos(angle) * radius;
          const y = cy + Math.sin(angle) * radius;
          return (
            <g key={mod.id}>
              <line
                className="pep-why-eco__link"
                x1={cx}
                y1={cy}
                x2={x}
                y2={y}
                stroke={`url(#${gradientId})`}
              />
              <circle className="pep-why-eco__pulse" r="2.2">
                <animateMotion
                  dur={`${7 + i * 0.55}s`}
                  repeatCount="indefinite"
                  path={`M${cx},${cy} L${x},${y}`}
                />
              </circle>
            </g>
          );
        })}
      </svg>

      <div className="pep-why-eco__core">
        <span className="pep-why-eco__core-logo">
          <PepLogo variant="compact" />
        </span>
        <span className="pep-why-eco__core-label">{centerLabel}</span>
      </div>

      {WHY_ECOSYSTEM_MODULES.map((mod, i) => {
        const angleDeg = (i / count) * 360 - 90;
        const angle = angleDeg * (Math.PI / 180);
        const left = 50 + Math.cos(angle) * 39.5;
        const top = 50 + Math.sin(angle) * 39.5;
        return (
          <div
            key={mod.id}
            className={`pep-why-eco__node pep-why-eco__node--${mod.tone}`}
            style={{ left: `${left}%`, top: `${top}%` }}
          >
            <span className="pep-why-eco__node-icon">
              <DomainIcon name={mod.icon} />
            </span>
            <span className="pep-why-eco__node-copy">
              <span className="pep-why-eco__node-label">
                {locale === "en" ? mod.labelEn : mod.labelFr}
              </span>
              <span className="pep-why-eco__node-sub">
                {locale === "en" ? mod.subtitleEn : mod.subtitleFr}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
